import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { apiFetch } from '@/api/client';
import { fechaCorta } from '@/lib/athena';

/**
 * TRAER UN HISTORIAL DE WHATSAPP.
 *
 * Esteban, 20-sep: *"yo tengo el derecho ahora mismo de alimentar el historial
 * de cualquier chat o de cualquier grupo de mi WhatsApp para extraer la
 * información necesaria para que el programa corra mejor"*.
 *
 * Y es la vía más rápida a datos reales: no hace falta vincular el número.
 *
 * ═══ DOS PASOS, Y EL DEL MEDIO ES EL QUE IMPORTA ═══
 *
 *   1. Se elige el archivo y se REVISA sin guardar nada.
 *   2. Se pregunta CUÁL DE LOS NOMBRES ERES TÚ, y recién ahí se guarda.
 *
 * En un export no hay forma de saber cuál nombre es el del dueño: el suyo
 * aparece como el de cualquiera. Y de eso depende todo lo demás — si se
 * equivoca, "te están esperando" señalaría sus propios mensajes.
 *
 * Preguntarlo DESPUÉS de guardar obligaría a corregir el rol de miles de filas
 * ya escritas.
 */

interface Revisado {
  mensajes: number;
  participantes: string[];
  pareceGrupo: boolean;
  noPudeLeer: number;
  desde: string | null;
  hasta: string | null;
}

interface Importado {
  conversationId: string;
  nombre: string;
  esGrupo: boolean;
  importados: number;
  repetidos: number;
  noPudeLeer: number;
}

export default function Importar() {
  const navegar = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [texto, setTexto] = useState('');
  const [nombre, setNombre] = useState('');
  const [revisado, setRevisado] = useState<Revisado | null>(null);
  const [yoSoy, setYoSoy] = useState<string>('');
  const [hecho, setHecho] = useState<Importado | null>(null);
  const [arrastrando, setArrastrando] = useState(false);

  const revisar = useMutation({
    mutationFn: (t: string) =>
      apiFetch<Revisado>('/api/importar/revisar', { method: 'POST', body: { texto: t } }),
    onSuccess: (r) => {
      setRevisado(r);
      // Se propone el que MÁS escribe como "tú", pero se puede cambiar: en un
      // chat propio suele acertar, y en un grupo de 15 casi nunca. Es una
      // sugerencia, no una decisión.
      setYoSoy(r.participantes[0] ?? '');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const importar = useMutation({
    mutationFn: () =>
      apiFetch<Importado>('/api/importar', {
        method: 'POST',
        body: { texto, nombre: nombre.trim(), yoSoy: yoSoy || undefined },
      }),
    onSuccess: (r) => {
      setHecho(r);
      void qc.invalidateQueries({ queryKey: ['chats'] });
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const leerArchivo = async (f: File) => {
    if (!f.name.endsWith('.txt')) {
      toast.error('Tiene que ser el .txt que exporta WhatsApp, no el .zip.');
      return;
    }
    const t = await f.text();
    setTexto(t);
    // El nombre sale del archivo: WhatsApp lo llama "Chat de WhatsApp con X".
    setNombre(
      f.name
        .replace(/\.txt$/i, '')
        .replace(/^Chat de WhatsApp con\s*/i, '')
        .replace(/^WhatsApp Chat with\s*/i, '')
        .trim(),
    );
    setHecho(null);
    revisar.mutate(t);
  };

  if (hecho) {
    return (
      <div className="space-y-3">
        <button
          onClick={() => navegar('/chats')}
          className="flex min-h-[44px] items-center gap-1 text-sm text-slate-600 dark:text-slate-300"
        >
          <ArrowLeft className="h-4 w-4" /> Ir a mis chats
        </button>

        <Card>
          <CardBody className="space-y-2">
            <p className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="h-5 w-5" /> {hecho.nombre} quedó importado.
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {hecho.importados.toLocaleString()} mensajes nuevos
              {hecho.repetidos > 0 && ` · ${hecho.repetidos.toLocaleString()} ya estaban`}
            </p>
            {hecho.noPudeLeer > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {hecho.noPudeLeer} líneas no se pudieron leer.
              </p>
            )}
            <p className="text-xs text-slate-500">
              Athena lo va a leer en su próxima pasada. Los mensajes importados
              viven en un canal aparte, en simulacro: no manda nada ni marca nada como visto.
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                setHecho(null);
                setTexto('');
                setRevisado(null);
                setNombre('');
              }}
            >
              Importar otro
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button
        onClick={() => navegar(-1)}
        className="flex min-h-[44px] items-center gap-1 text-sm text-slate-600 dark:text-slate-300"
      >
        <ArrowLeft className="h-4 w-4" /> Atrás
      </button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
          Traer un historial
        </h1>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          Sube un chat o un grupo exportado y Athena lo lee como si fuera suyo.
        </p>
      </div>

      <Card>
        <CardBody>
          <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            Cómo se exporta
          </p>
          <ol className="list-inside list-decimal space-y-1 text-sm text-slate-600 dark:text-slate-300">
            <li>Abre el chat o el grupo en tu teléfono</li>
            <li>Menú de los tres puntos → Más → Exportar chat</li>
            <li>
              Elige <strong>Sin archivos</strong> — con archivos pesa muchísimo y no hace falta
            </li>
            <li>Guarda el .txt y súbelo aquí</li>
          </ol>
        </CardBody>
      </Card>

      {/* El área de arrastre. También hay botón: en el teléfono no se arrastra. */}
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={(e) => {
          e.preventDefault();
          setArrastrando(false);
          const f = e.dataTransfer.files[0];
          if (f) void leerArchivo(f);
        }}
        className={cn(
          'flex min-h-[140px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition',
          arrastrando
            ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
            : 'border-slate-300 dark:border-slate-700',
        )}
      >
        <input
          type="file"
          accept=".txt,text/plain"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void leerArchivo(f);
          }}
        />
        <Upload className="h-6 w-6 text-slate-400" />
        <span className="text-sm text-slate-600 dark:text-slate-300">
          Arrastra el .txt aquí, o tócalo para elegirlo
        </span>
      </label>

      {revisar.isPending && (
        <p className="text-center text-sm text-slate-500">Leyendo el archivo…</p>
      )}

      {revisado && (
        <Card>
          <CardBody className="space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 shrink-0 text-slate-400" />
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {revisado.mensajes.toLocaleString()} mensajes
              </p>
              {revisado.pareceGrupo && (
                <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                  Grupo
                </Badge>
              )}
            </div>

            {revisado.desde && revisado.hasta && (
              <p className="text-xs text-slate-500">
                Desde {fechaCorta(revisado.desde)} hasta {fechaCorta(revisado.hasta)}
              </p>
            )}

            {/* Lo que no se pudo leer se dice. Un importador que se traga
                líneas en silencio deja al dueño creyendo que tiene todo. */}
            {revisado.noPudeLeer > 0 && (
              <p className="flex items-start gap-2 rounded bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {revisado.noPudeLeer} líneas no se pudieron leer. El resto entra igual.
              </p>
            )}

            <label className="block text-sm">
              <span className="mb-1 block font-medium">Cómo se llama este chat</span>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
              />
            </label>

            {/* EL PASO QUE IMPORTA. De esto depende que "te están esperando"
                mire hacia el lado correcto. */}
            <div>
              <p className="mb-1 text-sm font-medium">¿Cuál de estos eres tú?</p>
              <p className="mb-2 text-xs text-slate-500">
                Athena necesita saberlo para distinguir lo que dijiste de lo que te dijeron.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {revisado.participantes.map((p) => (
                  <button
                    key={p}
                    onClick={() => setYoSoy(p)}
                    className={cn(
                      'min-h-[36px] rounded-full border px-3 text-xs',
                      yoSoy === p
                        ? 'border-transparent bg-acento-lleno text-acento-sobre'
                        : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <Button
              className="w-full"
              disabled={!nombre.trim() || !yoSoy || importar.isPending}
              onClick={() => importar.mutate()}
            >
              {importar.isPending
                ? 'Importando…'
                : `Importar ${revisado.mensajes.toLocaleString()} mensajes`}
            </Button>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
